import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { 
  Eye, Trash2, Plus, TrendingUp, TrendingDown, Bell, 
  Package, DollarSign, AlertCircle, Loader2, Search,
  Heart, Clock, ArrowUpRight, ArrowDownRight, RefreshCw,
  Building2, ShoppingBag, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function BuyerDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [marketTrends, setMarketTrends] = useState(null);
  const [myInquiries, setMyInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Add to watchlist dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [newWatchItem, setNewWatchItem] = useState({
    product_name: '',
    hs_code: '',
    hs_description: '',
    target_price: '',
    email_alerts: true
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    if (!userData || userData.role !== 'buyer') {
      navigate('/dashboard');
      return;
    }
    setUser(userData);
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all data with individual error handling
      const [watchlistRes, alertsRes, trendsRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/buyer/watchlist`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/buyer/price-alerts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/buyer/market-trends`, { headers }).catch(() => ({ data: { trends: [] } })),
        axios.get(`${API}/buyer/inquiries`, { headers }).catch(() => ({ data: { inquiries: [] } }))
      ]);

      setWatchlist(Array.isArray(watchlistRes.data) ? watchlistRes.data : []);
      setPriceAlerts(Array.isArray(alertsRes.data) ? alertsRes.data : []);
      setMarketTrends(trendsRes.data?.trends || trendsRes.data || []);
      // Handle both array format and object format with inquiries property
      const inquiriesData = inquiriesRes.data;
      setMyInquiries(Array.isArray(inquiriesData) ? inquiriesData : (inquiriesData.inquiries || []));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleAddToWatchlist = async () => {
    if (!newWatchItem.product_name || !newWatchItem.hs_code) {
      toast.error('Please fill in product name and HS code');
      return;
    }

    setAddingItem(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/buyer/watchlist`, {
        product_name: newWatchItem.product_name,
        hs_code: newWatchItem.hs_code,
        hs_description: newWatchItem.hs_description,
        target_price: parseFloat(newWatchItem.target_price) || null,
        email_alerts: newWatchItem.email_alerts
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Added to watchlist!');
      setShowAddDialog(false);
      setNewWatchItem({
        product_name: '',
        hs_code: '',
        hs_description: '',
        target_price: '',
        email_alerts: true
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to watchlist');
    } finally {
      setAddingItem(false);
    }
  };

  const handleRemoveFromWatchlist = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/buyer/watchlist/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removed from watchlist');
      setWatchlist(watchlist.filter(item => item.id !== itemId));
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleMarkAlertRead = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/buyer/price-alerts/${alertId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPriceAlerts(priceAlerts.map(alert => 
        alert.id === alertId ? { ...alert, is_read: true } : alert
      ));
    } catch (error) {
      console.error('Failed to mark alert as read');
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `₹${price.toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const unreadAlerts = priceAlerts.filter(a => !a.is_read).length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Buyer Dashboard</h1>
            <p className="text-muted-foreground">Track products and get price alerts</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-watchlist-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add to Watchlist
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Watching</p>
                  <p className="text-2xl font-bold">{watchlist.length}</p>
                </div>
                <Eye className="h-8 w-8 text-primary opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Price Alerts</p>
                  <p className="text-2xl font-bold">{unreadAlerts}</p>
                </div>
                <Bell className={`h-8 w-8 ${unreadAlerts > 0 ? 'text-amber-500' : 'text-muted-foreground'}`} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Matching Sellers</p>
                  <p className="text-2xl font-bold">
                    {watchlist.reduce((acc, item) => acc + (item.matching_sellers?.length || 0), 0)}
                  </p>
                </div>
                <Building2 className="h-8 w-8 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Inquiries</p>
                  <p className="text-2xl font-bold">{marketTrends?.active_inquiries || 0}</p>
                </div>
                <ShoppingBag className="h-8 w-8 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="watchlist" className="space-y-4">
          <TabsList>
            <TabsTrigger value="watchlist" data-testid="watchlist-tab">
              <Eye className="h-4 w-4 mr-2" />
              Watchlist ({watchlist.length})
            </TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="inquiries-tab">
              <ShoppingBag className="h-4 w-4 mr-2" />
              My Inquiries ({myInquiries.length})
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="alerts-tab">
              <Bell className="h-4 w-4 mr-2" />
              Price Alerts {unreadAlerts > 0 && (
                <Badge variant="destructive" className="ml-2">{unreadAlerts}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="trends" data-testid="trends-tab">
              <TrendingUp className="h-4 w-4 mr-2" />
              Market Trends
            </TabsTrigger>
          </TabsList>

          {/* Watchlist Tab */}
          <TabsContent value="watchlist" className="space-y-4">
            {watchlist.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">Your watchlist is empty</h3>
                    <p className="text-muted-foreground mb-4">
                      Add products to track prices and get alerts when they change
                    </p>
                    <Button onClick={() => setShowAddDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Item
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {watchlist.map((item) => (
                  <Card key={item.id} data-testid={`watchlist-item-${item.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{item.product_name}</h3>
                            <Badge variant="outline">{item.hs_code}</Badge>
                            {item.email_alerts && (
                              <Badge variant="secondary">
                                <Bell className="h-3 w-3 mr-1" />
                                Alerts On
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {item.hs_description || 'No description'}
                          </p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              <DollarSign className="h-4 w-4 text-muted-foreground" />
                              <span>Current: {formatPrice(item.current_price)}</span>
                            </div>
                            {item.target_price && (
                              <div className="flex items-center gap-1">
                                <TrendingDown className="h-4 w-4 text-green-500" />
                                <span>Target: {formatPrice(item.target_price)}</span>
                              </div>
                            )}
                            {item.price_change_percent !== undefined && (
                              <div className={`flex items-center gap-1 ${
                                item.price_change_percent > 0 ? 'text-red-500' : 'text-green-500'
                              }`}>
                                {item.price_change_percent > 0 ? (
                                  <ArrowUpRight className="h-4 w-4" />
                                ) : (
                                  <ArrowDownRight className="h-4 w-4" />
                                )}
                                <span>{Math.abs(item.price_change_percent)}%</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              <span>Added {formatDate(item.created_at)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Matching Sellers */}
                        <div className="lg:w-64">
                          {item.matching_sellers && item.matching_sellers.length > 0 ? (
                            <div>
                              <p className="text-xs text-muted-foreground mb-2">
                                {item.matching_sellers.length} matching sellers
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {item.matching_sellers.slice(0, 3).map((seller) => (
                                  <Button
                                    key={seller.id}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate(`/product/${seller.id}`)}
                                    className="text-xs"
                                  >
                                    {seller.seller_name?.slice(0, 15)}...
                                  </Button>
                                ))}
                                {item.matching_sellers.length > 3 && (
                                  <Badge variant="secondary">+{item.matching_sellers.length - 3}</Badge>
                                )}
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">No matching sellers yet</p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/marketplace')}
                          >
                            <Search className="h-4 w-4 mr-1" />
                            Find Sellers
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFromWatchlist(item.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`remove-watchlist-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Inquiries Tab */}
          <TabsContent value="inquiries" className="space-y-4">
            {myInquiries.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No inquiries yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Browse the marketplace and send inquiries to sellers
                    </p>
                    <Button onClick={() => navigate('/marketplace')}>
                      <Search className="h-4 w-4 mr-2" />
                      Browse Marketplace
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {myInquiries.map((inquiry) => (
                  <Card key={inquiry.id} data-testid={`inquiry-${inquiry.id}`}>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{inquiry.product_title || 'Product Inquiry'}</h3>
                            <Badge variant={
                              inquiry.status === 'pending' ? 'secondary' :
                              inquiry.status === 'responded' ? 'default' :
                              inquiry.status === 'closed' ? 'outline' : 'secondary'
                            }>
                              {inquiry.status || 'Pending'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            To: {inquiry.seller_name || 'Seller'}
                          </p>
                          <p className="text-sm line-clamp-2">{inquiry.message}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Package className="h-3 w-3" />
                              Qty: {inquiry.quantity} {inquiry.unit}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(inquiry.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/inquiries?id=${inquiry.id}`)}
                            data-testid={`view-inquiry-${inquiry.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/product/${inquiry.product_id}`)}
                          >
                            Product
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Price Alerts Tab */}
          <TabsContent value="alerts" className="space-y-4">
            {priceAlerts.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No price alerts yet</h3>
                    <p className="text-muted-foreground">
                      You'll receive alerts when prices change for your watched products
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {priceAlerts.map((alert) => (
                  <Card 
                    key={alert.id} 
                    className={alert.is_read ? 'opacity-60' : ''}
                    data-testid={`alert-${alert.id}`}
                  >
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${
                            alert.change_type === 'increase' ? 'bg-red-100' : 'bg-green-100'
                          }`}>
                            {alert.change_type === 'increase' ? (
                              <ArrowUpRight className="h-4 w-4 text-red-600" />
                            ) : (
                              <ArrowDownRight className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{alert.product_name}</p>
                            <p className="text-sm text-muted-foreground">
                              Price {alert.change_type === 'increase' ? 'increased' : 'decreased'} by {Math.abs(alert.change_percent)}%
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">
                              {formatPrice(alert.old_price)} → {formatPrice(alert.new_price)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(alert.created_at)}
                            </p>
                          </div>
                          {!alert.is_read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkAlertRead(alert.id)}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Market Trends Tab */}
          <TabsContent value="trends" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Market Overview</CardTitle>
                <CardDescription>
                  Based on your watchlist and platform activity
                </CardDescription>
              </CardHeader>
              <CardContent>
                {marketTrends ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <p className="text-3xl font-bold text-primary">{marketTrends.watchlist_count || 0}</p>
                      <p className="text-sm text-muted-foreground">Products Watched</p>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <p className="text-3xl font-bold text-green-600">{marketTrends.total_sellers || 0}</p>
                      <p className="text-sm text-muted-foreground">Available Sellers</p>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <p className="text-3xl font-bold text-blue-600">{marketTrends.active_inquiries || 0}</p>
                      <p className="text-sm text-muted-foreground">Your Inquiries</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Add items to your watchlist to see market trends
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => navigate('/marketplace')}>
                  <Search className="h-4 w-4 mr-2" />
                  Browse Marketplace
                </Button>
                <Button variant="outline" onClick={() => navigate('/inquiries')}>
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  View Inquiries
                </Button>
                <Button variant="outline" onClick={() => setShowAddDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Watchlist
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add to Watchlist Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-watchlist-dialog">
          <DialogHeader>
            <DialogTitle>Add to Watchlist</DialogTitle>
            <DialogDescription>
              Track a product and get notified when prices change
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="product_name">Product Name *</Label>
              <Input
                id="product_name"
                value={newWatchItem.product_name}
                onChange={(e) => setNewWatchItem({ ...newWatchItem, product_name: e.target.value })}
                placeholder="e.g., Basmati Rice, Cotton Fabric"
                data-testid="watch-product-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hs_code">HS Code *</Label>
              <Input
                id="hs_code"
                value={newWatchItem.hs_code}
                onChange={(e) => setNewWatchItem({ ...newWatchItem, hs_code: e.target.value })}
                placeholder="e.g., 1006.30, 5201.00"
                data-testid="watch-hs-code"
              />
              <p className="text-xs text-muted-foreground">
                Harmonized System code for the product category
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hs_description">Description</Label>
              <Input
                id="hs_description"
                value={newWatchItem.hs_description}
                onChange={(e) => setNewWatchItem({ ...newWatchItem, hs_description: e.target.value })}
                placeholder="e.g., Semi-milled or wholly milled rice"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="target_price">Target Price (₹)</Label>
              <Input
                id="target_price"
                type="number"
                value={newWatchItem.target_price}
                onChange={(e) => setNewWatchItem({ ...newWatchItem, target_price: e.target.value })}
                placeholder="Alert me when price drops to this"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Email Alerts</Label>
                <p className="text-xs text-muted-foreground">
                  Get notified when prices change
                </p>
              </div>
              <Switch
                checked={newWatchItem.email_alerts}
                onCheckedChange={(checked) => setNewWatchItem({ ...newWatchItem, email_alerts: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddToWatchlist} disabled={addingItem} data-testid="confirm-add-watchlist">
              {addingItem ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
              ) : (
                <><Plus className="h-4 w-4 mr-2" /> Add to Watchlist</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
